import { UploadProgressDialogComponent } from '@adgate/common/components/upload-progress-dialog-component/upload-progress-dialog-component';
import { inject, Service } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ErrorDialogComponent } from '@adgate/common/components/error-dialog-component/error-dialog-component';
import { ConfirmDialogComponent, ConfirmDialogData } from '@adgate/common/components/confirm-dialog-component/confirm-dialog-component';
import { MediaPlayerDialogComponent, MediaPlayerDialogData } from '@adgate/common/components/media-player-dialog-component/media-player-dialog-component';
import { LogDialogComponent, LogDialogData } from '@adgate/common/components/log-dialog-component/log-dialog-component';
import { ActionDialogComponent, ActionDialogData } from '@adgate/common/components/action-dialog-component/action-dialog-component';
import { Status } from '@adgate/common/types/status.type';
import { Action } from '@adgate/common/types/action.type';
import { FileUploadModel } from '@adgate/common/services/network-service';
import { SpecCheckReportDialogComponent } from '../components/spec-check-report-dialog-component/spec-check-report-dialog-component';
import { UserProfileDialogComponent } from '@adgate/modules/main/dialogs/user-profile-dialog-component/user-profile-dialog-component';
import { ThinkingDialogComponent } from '@adgate/common/components/thinking-dialog-component/thinking-dialog-component';

@Service()
export class DialogService
{
  public dialogRef: MatDialog = inject(MatDialog);
  public dialogHandle: any;
  private progressCount: number = 0;

  public openErrorDialog( message: string )
  {
    this.hideProgress();
    this.openDialog('error', message);
  }

  public openInfoDialog( message: string )
  {
    this.hideProgress();
    this.openDialog('info', message);
  }

  private openDialog( severity: string, message: string )
  {
    let errorDialogData = {
      severity: severity,
      message: message
    };

    this.hideProgress();
    this.dialogRef.open(ErrorDialogComponent, {data: errorDialogData, width:'600px'} );
  }

  public openConfirmDialog( message: string, options?: Partial<ConfirmDialogData> ): MatDialogRef<ConfirmDialogComponent, boolean>
  {
    const confirmDialogData: ConfirmDialogData = {
      message: message,
      title: options?.title,
      subtitle: options?.subtitle,
      confirmText: options?.confirmText || 'Confirm',
      cancelText: options?.cancelText || 'Cancel',
      severity: options?.severity || 'warning'
    };

    return this.dialogRef.open( ConfirmDialogComponent, {
      data: confirmDialogData,
      width: '600px',
      maxWidth: '90vw',
      disableClose: false
    });
  }  

  public openAnyDialog( dialogComponent: any, dialogConfig: any ): MatDialogRef<any,any>
  {
    return this.dialogRef.open( dialogComponent, dialogConfig );
  }  

  public openMediaPlayerDialog( mediaPlayerData: MediaPlayerDialogData )
  {
    this.dialogRef.open( MediaPlayerDialogComponent, {data: mediaPlayerData, height: '650px', width: '880px', minWidth: '880px'} );
  }

  public openHistoryDialog( objectType: number, objectId: number, objectDescription: string | null, objectStatus: Status | null, addMessageAllowed: boolean )
  {
    let dialogData: LogDialogData = {
      objectType: objectType,
      objectId: objectId,
      objectDescription: objectDescription,
      objectStatus: objectStatus,
      allowAdd: addMessageAllowed
    };

    this.dialogRef.open(LogDialogComponent, {
      data: dialogData,
      width: '700px',
      maxWidth: '90vw',
      disableClose: false
    });
  }

  public openActionDialog( objectType: number, actionList: Action[], statusList: Status[] )
  {
    let data: ActionDialogData = new ActionDialogData();
    data.actionList = actionList;
    data.statusList = statusList;
    data.objectType = objectType;

    return this.dialogRef.open(ActionDialogComponent, {data: data, width: '600px', disableClose: true} );
  }

  public openUploadProgressDialog( file: FileUploadModel )
  {
    return this.dialogRef.open(UploadProgressDialogComponent, {data: file, width:'600px', disableClose: true} );
  }

  public openSpecCheckReportDialog()
  {
    return this.dialogRef.open(SpecCheckReportDialogComponent, {data: null, width:'600px', disableClose: true} );
  }

  public openUserProfileDialog()
  {    
    return this.dialogRef.open(UserProfileDialogComponent, {data: null, width:'600px', disableClose: true} );
  }

  public openChangePasswordDialog()
  {
    return this.dialogRef.open(UserProfileDialogComponent, {data: null, width:'600px', disableClose: true} );
  }

  showProgress()
  {
    this.progressCount++;

    // Only open the dialog if it's not already open
    if (this.progressCount === 1) {
      this.dialogHandle = this.dialogRef.open(ThinkingDialogComponent, {
        width: '500px',
        maxWidth: '90vw',
        disableClose: true
      });
    }
  }

  hideProgress()
  {
    this.progressCount--;

    // Only close the dialog when all progress operations are done
    if (this.progressCount <= 0) {
      this.progressCount = 0; // Reset to prevent negative values
      if (this.dialogHandle != null) {
        this.dialogHandle.close();
        this.dialogHandle = null;
      }
    }
  }
}
